import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser, getOrganisations, getOrganisationProfiles, lookupUserByEmail, addUserToOrganisation } from '../api';
import Breadcrumb from '../components/Breadcrumb';

const ACCESS_LABELS = { READ: 'Read', WRITE: 'Read + Write', DELETE: 'Full Access' };

export default function AddUserPage() {
  const navigate = useNavigate();
  // Phase: 'email' | 'existing' | 'new' | 'done'
  const [phase, setPhase] = useState('email');
  const [email, setEmail] = useState('');
  const [existingUser, setExistingUser] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    organisationId: '',
    profileId: '',
  });

  const [organisations, setOrganisations] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [message, setMessage] = useState('');
  const [createdName, setCreatedName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  useEffect(() => {
    getOrganisations().then(setOrganisations).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.organisationId) { setProfiles([]); return; }
    setLoadingProfiles(true);
    getOrganisationProfiles(form.organisationId)
      .then(setProfiles)
      .catch(() => setProfiles([]))
      .finally(() => setLoadingProfiles(false));
  }, [form.organisationId]);

  const selectedProfile = profiles.find((p) => String(p.id) === String(form.profileId)) || null;

  const handleCheck = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const user = await lookupUserByEmail(email);
      setExistingUser(user);
      setPhase('existing');
    } catch {
      setExistingUser(null);
      setPhase('new');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPhase('email');
    setEmail('');
    setExistingUser(null);
    setForm({ firstName: '', lastName: '', organisationId: '', profileId: '' });
    setError('');
    setMessage('');
  };

  const handleChange = (e) => {
    if (e.target.name === 'organisationId') {
      setForm({ ...form, organisationId: e.target.value, profileId: '' });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await addUserToOrganisation(
        Number(form.organisationId),
        existingUser.id,
        Number(form.profileId),
      );
      setMessage(`"${existingUser.firstName} ${existingUser.lastName}" has been added to the organisation`);
      setForm({ ...form, organisationId: '', profileId: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const data = await createUser({
        email,
        firstName: form.firstName,
        lastName: form.lastName,
        organisationId: Number(form.organisationId),
        profileId: Number(form.profileId),
      });
      setCreatedName(`${data.firstName} ${data.lastName}`);
      setPhase('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Users', to: '/organisation-users' },
          { label: 'Add User' },
        ]} />
        <h2>Add User</h2>
        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        {/* Phase 1: Email entry */}
        {phase === 'email' && (
          <form onSubmit={handleCheck}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="user@example.com"
              />
              <small>Enter the user's email to check if they already have an account</small>
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Checking...' : 'Check'}
            </button>
          </form>
        )}

        {/* Phase 2a: Existing user — assign to org */}
        {phase === 'existing' && existingUser && (
          <form onSubmit={handleAssign}>
            <div className="alert alert-info">
              User found: <strong>{existingUser.firstName} {existingUser.lastName}</strong> ({existingUser.email})
            </div>
            <div className="form-group">
              <label>Organisation</label>
              <select name="organisationId" value={form.organisationId} onChange={handleChange} required>
                <option value="">Select organisation...</option>
                {organisations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Profile</label>
              <select name="profileId" value={form.profileId} onChange={handleChange} required disabled={loadingProfiles}>
                <option value="">{loadingProfiles ? 'Loading profiles...' : 'Select profile...'}</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.description ? ` — ${p.description}` : ''}</option>
                ))}
              </select>
              {loadingProfiles && <small style={{ color: '#4361ee' }}>Loading profiles for this organisation...</small>}
              {!loadingProfiles && <small>The user's role within the selected organisation</small>}
            </div>
            {selectedProfile?.permissions?.length > 0 && (
              <div className="profile-permissions-preview">
                <label>Permissions for "{selectedProfile.name}"</label>
                <ul>
                  {selectedProfile.permissions.map((perm) => (
                    <li key={perm.featureCode}>
                      <span className="perm-feature">{perm.featureName}</span>
                      <span className={`perm-level perm-level-${perm.accessLevel.toLowerCase()}`}>{ACCESS_LABELS[perm.accessLevel] || perm.accessLevel}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Adding...' : 'Add to Organisation'}
            </button>
            <button type="button" className="btn btn-link btn-full" onClick={handleReset}>
              Use a different email
            </button>
          </form>
        )}

        {/* Phase: Done — user created */}
        {phase === 'done' && (
          <div>
            <div className="alert alert-success">
              User "{createdName}" has been created successfully. They will receive an email with a link to set their password.
            </div>
            <button className="btn btn-primary btn-full" onClick={handleReset} style={{ marginBottom: 8 }}>
              Add Another
            </button>
            <button className="btn btn-link btn-full" onClick={() => navigate('/')}>
              Return to Dashboard
            </button>
          </div>
        )}

        {/* Phase 2b: New user — full creation form */}
        {phase === 'new' && (
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} readOnly />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Organisation</label>
              <select name="organisationId" value={form.organisationId} onChange={handleChange} required>
                <option value="">Select organisation...</option>
                {organisations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Profile</label>
              <select name="profileId" value={form.profileId} onChange={handleChange} required disabled={loadingProfiles}>
                <option value="">{loadingProfiles ? 'Loading profiles...' : 'Select profile...'}</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.description ? ` — ${p.description}` : ''}</option>
                ))}
              </select>
              {loadingProfiles && <small style={{ color: '#4361ee' }}>Loading profiles for this organisation...</small>}
              {!loadingProfiles && <small>The user's role within the selected organisation</small>}
            </div>
            {selectedProfile?.permissions?.length > 0 && (
              <div className="profile-permissions-preview">
                <label>Permissions for "{selectedProfile.name}"</label>
                <ul>
                  {selectedProfile.permissions.map((perm) => (
                    <li key={perm.featureCode}>
                      <span className="perm-feature">{perm.featureName}</span>
                      <span className={`perm-level perm-level-${perm.accessLevel.toLowerCase()}`}>{ACCESS_LABELS[perm.accessLevel] || perm.accessLevel}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
            <button type="button" className="btn btn-link btn-full" onClick={handleReset}>
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
